/**
 * `useSyntaxHighlighter` — produce syntax-highlighted lines from a
 * string of source code.
 *
 * The hook is intentionally library-agnostic: it talks only to the
 * `highlight` contract exposed by the adapter file. Swapping the
 * underlying highlighter (e.g. refractor → Shiki) means writing a
 * new adapter file with the same shape; this hook does not need to
 * change.
 *
 * The returned `lines` is *always* populated. When real highlighting
 * is unavailable (no language, the `disabled` gate is set, the
 * highlighter has not yet resolved, the load errored, etc.) we
 * synthesize a trivial single-token-per-line fallback. Consumers
 * therefore have a single render path; the "highlighted vs not"
 * difference shows up only as token classes.
 */

import { useEffect, useMemo, useState } from 'react';

import { highlight } from '../refractor-adapter';
import type { SupportedLanguage } from '../supported-languages';
import type { HighlightedLine } from '../types/highlighted-line';
import { splitLines } from '../utils/split-lines';

type Options = {
	code: string;
	language: SupportedLanguage | undefined;
	/**
	 * When `true`, the hook returns the plain-text fallback and does
	 * NOT trigger a grammar load. This exists so callers can gate the
	 * load + highlight cost — both can be expensive for large files.
	 *
	 * Defaults to `false` (highlighting active).
	 */
	disabled?: boolean;
};

type Result = {
	lines: HighlightedLine[];
	error?: Error;
};

/**
 * Build a trivial `HighlightedLine[]` from raw source — one token per
 * line, no classes. Used as the fallback whenever real highlighting
 * isn't available.
 *
 * Empty source produces a single empty line so consumers don't have
 * to special-case empty input.
 */
const toPlainLines = (code: string): HighlightedLine[] =>
	splitLines(code).map((line) => (line.length > 0 ? [{ text: line }] : []));

export const useSyntaxHighlighter = ({ code, language, disabled = false }: Options): Result => {
	const [highlighted, setHighlighted] = useState<HighlightedLine[] | undefined>();
	const [error, setError] = useState<Error | undefined>();

	useEffect(() => {
		if (disabled || !language) {
			setHighlighted(undefined);
			setError(undefined);
			return undefined;
		}

		let cancelled = false;

		const highlightCode = async () => {
			try {
				const result = await highlight(code, language);
				if (!cancelled) {
					setHighlighted(result);
					setError(undefined);
				}
			} catch (err) {
				if (!cancelled) {
					setHighlighted(undefined);
					setError(err instanceof Error ? err : new Error(String(err)));
				}
			}
		};

		highlightCode();

		return () => {
			cancelled = true;
		};
	}, [code, disabled, language]);

	const plainLines = useMemo(() => toPlainLines(code), [code]);
	const lines = highlighted ?? plainLines;

	return error ? { lines, error } : { lines };
};
